import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ArrowRight, Volume2, VolumeX, Mail, X, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADVENTURES = [
  {
    slug: 'vent-to-a-dog',
    title: 'Vent to a Dog',
    tagline: 'An antidote to AI psychosis.',
    kind: 'Digital · Hyperlocal to your heart',
    image: '/almanac/dogs/hebrew_bark_poster.jpg',
    video: '/almanac/dogs/hebrew_bark.mp4',
    poster: '/almanac/dogs/hebrew_bark_poster.jpg',
    live: true,
  },
  {
    slug: 'daily-duel',
    title: 'Seat Fighter',
    tagline: "Every leader's fighting for a seat — your click decides who falls.",
    kind: 'Digital · Live',
    image: '/almanac/adventures/daily-duel.jpg',
    live: true,
    href: '/seat-fighter',
  },
  {
    slug: 'how-far-from-your-star',
    title: 'How Far From Your Star?',
    tagline: 'Find your true calling — written in your birth star.',
    kind: 'Digital · Coming soon',
    image: '/almanac/adventures/how-far-from-your-star.jpg',
    live: false,
  },
  {
    slug: 'three-idiots',
    title: '3+1diots',
    titleJsx: (<span>3<sup className="text-[0.5em] align-super ml-0.5">+1diots</sup></span>),
    tagline: '3+1 types of idiots exist. Which one are you?',
    kind: 'Digital · Coming soon',
    image: '/almanac/adventures/three-idiots.jpg',
    live: false,
  },
];

const AdventureGrid = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);
  const [notifyAdv, setNotifyAdv] = useState(null); // coming-soon adventure being subscribed to
  const [notifyEmail, setNotifyEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [doneSlugs, setDoneSlugs] = useState([]);
  const [ventCount, setVentCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dog-vent/stats/today`)
      .then((r) => setVentCount(r.data.count))
      .catch(() => {});
  }, []);

  const toggleSound = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setSoundOn(!v.muted);
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    const em = notifyEmail.trim().toLowerCase();
    if (!em || !em.includes('@') || !em.includes('.')) {
      toast.error('Enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribers`, { email: em, source: `coming_soon_${notifyAdv.slug}` });
      setDoneSlugs((s) => [...s, notifyAdv.slug]);
      toast.success(`You're on the list — we'll invite you when ${notifyAdv.title} lands! 🐾`);
      setNotifyEmail('');
      setTimeout(() => setNotifyAdv(null), 1400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your email. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6" data-testid="adventure-grid">
      {ADVENTURES.map((adv, i) => (
        <motion.button
          key={adv.slug}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: i * 0.08 }}
          whileHover={adv.live ? { y: -6 } : { y: -4 }}
          onClick={() => (adv.live ? navigate(adv.href || '/vent-to-dog') : setNotifyAdv(adv))}
          data-testid={`adventure-card-${adv.slug}`}
          className="group relative overflow-hidden rounded-3xl text-left aspect-[4/3] border border-white/10 cursor-pointer"
        >
          {adv.video ? (
            <video
              ref={videoRef}
              src={adv.video}
              poster={adv.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              onMouseEnter={(e) => { e.currentTarget.muted = false; e.currentTarget.play().catch(() => {}); setSoundOn(true); }}
              onMouseLeave={(e) => { e.currentTarget.muted = true; setSoundOn(false); }}
              data-testid={`adventure-video-${adv.slug}`}
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                adv.live ? 'group-hover:scale-110' : 'scale-100'
              }`}
            />
          ) : (
            <img
              src={adv.image}
              alt={adv.title}
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
                adv.live ? 'group-hover:scale-110' : 'scale-100'
              } ${adv.live ? '' : 'grayscale-[35%]'}`}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

          {adv.video && (
            <span
              role="button"
              tabIndex={0}
              onClick={toggleSound}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSound(e); }}
              aria-label={soundOn ? 'Mute Hebrew' : 'Hear Hebrew bark'}
              data-testid="adventure-sound-toggle"
              className="absolute top-4 left-4 z-10 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
            >
              {soundOn ? <Volume2 className="w-4 h-4 text-emerald-300" /> : <VolumeX className="w-4 h-4 text-white/80" />}
            </span>
          )}

          {!adv.live && (
            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15">
              <Lock className="w-3 h-3 text-white/70" />
              <span className="text-[10px] tracking-[0.15em] uppercase text-white/70">Soon</span>
            </div>
          )}
          {adv.live && (
            <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-emerald-300/50">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] tracking-[0.15em] uppercase text-emerald-200 font-semibold">{adv.slug === 'daily-duel' ? 'Live · 3 free daily' : 'Live · 1st free'}</span>
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">{adv.kind}</p>
            <h3 className="font-adventure font-bold text-2xl text-white mb-1">{adv.titleJsx || adv.title}</h3>
            <p className="text-sm text-white/70 mb-3">{adv.tagline}</p>
            {adv.slug === 'vent-to-a-dog' && adv.live && ventCount !== null && (
              <p className="text-xs text-emerald-300/90 mb-3 flex items-center gap-1.5" data-testid="vent-count-today">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {ventCount > 0
                  ? `${ventCount} ${ventCount === 1 ? 'friend has' : 'friends have'} vented today`
                  : 'Be the first to vent today'}
              </p>
            )}
            {adv.live ? (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 group-hover:gap-3 transition-all">
                Begin adventure <ArrowRight className="w-4 h-4" />
              </span>
            ) : doneSlugs.includes(adv.slug) ? (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-300">
                <Check className="w-4 h-4" /> You're on the list
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 group-hover:text-emerald-300 group-hover:gap-3 transition-all">
                <Mail className="w-4 h-4" /> Get invited
              </span>
            )}
          </div>
        </motion.button>
      ))}

      <AnimatePresence>
        {notifyAdv && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setNotifyAdv(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm"
            data-testid="notify-modal"
          >
            <motion.div
              initial={{ scale: 0.94, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-7 max-w-sm w-full border border-white/10 relative text-center"
            >
              <button
                onClick={() => setNotifyAdv(null)}
                data-testid="notify-close"
                className="absolute top-4 right-4 text-zinc-500 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <p className="text-[11px] tracking-[0.3em] uppercase text-emerald-400 mb-3">{notifyAdv.title}</p>
              <h3 className="font-adventure font-bold text-2xl text-white mb-2">Wanna know when the next adventure lands?</h3>
              <p className="text-sm text-zinc-400 mb-5">Drop your email and you'll be invited the moment {notifyAdv.title} goes live. 🐾</p>
              {doneSlugs.includes(notifyAdv.slug) ? (
                <div className="flex items-center gap-2 justify-center text-emerald-300 py-3" data-testid="notify-done">
                  <Check className="w-5 h-5" /> You're on the list!
                </div>
              ) : (
                <form onSubmit={submitNotify} className="space-y-3">
                  <div className="relative">
                    <Mail className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="you@email.com"
                      autoFocus
                      data-testid="notify-email-input"
                      className="w-full bg-black/30 border border-white/10 rounded-full pl-9 pr-3 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-emerald-400/50 outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !notifyEmail.trim()}
                    data-testid="notify-submit"
                    className="w-full py-3 rounded-full bg-emerald-400 text-[#08130d] font-heading font-bold text-sm hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Invite me'}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdventureGrid;
